import React, { useState, useRef, useEffect } from "react";
import { X } from "lucide-react";
import CustomDropdown from "../../Ui_Components/CustomDropdown";
import {
  getAllGuidelineCategories,
  uploadGuideline,
} from "../../../services/subPlans";
import { useAuth } from "../../../context/auth/AuthContext";
import { sendPlanNotificationToUser } from "../../../utils/oneSignalNotification";
import { Button } from "../../Ui_Components/Button";

interface GuidelinesPlanCreatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => void;
  userId: number;
  onShowToast?: (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message?: string,
    duration?: number
  ) => void;
}

interface CategoryOption {
  value: string;   // guideline_category_name — used as the dropdown value key
  label: string;
  id: number;      // guideline_category_id — needed for the upload API
}

const GuidelinesPlanCreatePopup: React.FC<GuidelinesPlanCreatePopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userId,
  onShowToast,
}) => {
  const { user } = useAuth();
  const nutristId = user?.nutrist_id;

  const [isCreating, setIsCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");           // stores category name (for display)
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null); // stores id (for API)
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);

  // Fetch categories when popup opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchCategories = async () => {
      setIsCategoriesLoading(true);
      try {
        const apiResponse = await getAllGuidelineCategories();

        if (
          (apiResponse.success === 1 || apiResponse.success === "1") &&
          Array.isArray(apiResponse.data)
        ) {
          const mapped: CategoryOption[] = apiResponse.data.map((item: any) => ({
            value: item.guideline_category_name,
            label: item.guideline_category_name,
            id: item.guideline_category_id,
          }));
          setCategoryOptions(mapped);
        } else {
          throw new Error(apiResponse.message || "Failed to fetch categories");
        }
      } catch (err) {
        console.error("❌ Error fetching guideline categories:", err);
        onShowToast?.(
          "error",
          "Failed to load categories",
          "Could not fetch guideline categories. Please try again."
        );
      } finally {
        setIsCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, [isOpen]);

  const isFormComplete = () =>
  title.trim() !== "" &&
  category !== "" &&
  uploadedFile !== null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type !== "application/pdf") {
        onShowToast?.(
          "error",
          "Invalid file type",
          "Only PDF files are allowed. Please choose a .pdf file."
        );
        // Reset so the same file can trigger onChange again if needed
        e.target.value = "";
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleClose = () => {
    setTitle("");
    setCategory("");
    setSelectedCategoryId(null);
    setUploadedFile(null);
    onClose();
  };

  const handleSendToClient = async () => {
    if (!isFormComplete()) {
      onShowToast?.(
        "warning",
        "Incomplete form",
        "Please fill in the title and select a category"
      );
      return;
    }

    if (selectedCategoryId === null) {
      onShowToast?.("warning", "Invalid category", "Please re-select a category");
      return;
    }

    setIsCreating(true);

    try {
      // Build FormData exactly as the API expects
      const formData = new FormData();
      formData.append("title", title);
      formData.append("category_id", String(selectedCategoryId));
      formData.append("created_by_id", String(nutristId));
      formData.append("user_id", String(userId));
      if (uploadedFile) {
        formData.append("uploaded_file", uploadedFile);
      }

      const apiResponse = await uploadGuideline(formData);
      console.log("✅ Guideline upload response:", apiResponse);

      if (apiResponse.success === "1" || apiResponse.success === 1) {
        sendPlanNotificationToUser(userId, "guideline_plan");

        const newGuideline = {
          id: apiResponse.guideline_id ?? Date.now(),
          name: title,
          category: category,
          created_by: "You",
          created_on: new Date().toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }),
          status: "Active" as const,
          file: uploadedFile?.name || null,
        };

        onSubmit(newGuideline);
        handleClose();
        onShowToast?.(
          "success",
          "Guideline plan created",
          "Your guideline plan has been successfully created"
        );
      } else {
        throw new Error(apiResponse.message || "Upload failed");
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to upload guideline";
      console.error("❌ Error uploading guideline:", err);
      onShowToast?.("error", "Upload failed", msg);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="pt-5 py-3 mx-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg 3xl:text-[20px] font-semibold font-inter text-[#1F1F1F]">
            Create guideline plan
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            {/* Title */}
            <div>
              <label className="block text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] mb-2">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter title"
                className="w-full px-3 py-2 border border-[#E5E5EA] rounded-md text-sm font-inter text-[#1F1F1F] placeholder-[#C7C7CC] focus:outline-none focus:border-[#0DB5B5] transition-colors"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] mb-2">
                Category
              </label>
              <CustomDropdown
                options={categoryOptions}
                value={category}
                onChange={(value) => {
                  setCategory(value);
                  const matched = categoryOptions.find((opt) => opt.value === value);
                  setSelectedCategoryId(matched?.id ?? null);
                }}
                placeholder={
                  isCategoriesLoading ? "Loading categories..." : "Select category"
                }
                searchable={false}
                disabled={isCategoriesLoading}
              />
            </div>
          </div>

          {/* Upload file */}
          <div>
            <label className="block text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F] mb-2">
              Upload file <span className="text-[#8E8E93] font-normal">(PDF only)</span>
            </label>
            <div className="flex items-center border border-[#D1D5DB] rounded-[18px] overflow-hidden">
              <button
                type="button"
                onClick={handleChooseFile}
                className="flex-shrink-0 px-4 py-2 bg-[#1F1F1F] text-white text-sm font-medium font-inter hover:bg-[#333333] transition-colors"
              >
                Choose File
              </button>
              <span className="px-4 text-sm font-inter text-[#8E8E93] flex-1 truncate">
                {uploadedFile ? uploadedFile.name : "No file chosen"}
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,application/pdf"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="py-4 mx-6 border-t border-gray-200 flex items-center justify-end gap-4 flex-shrink-0">
          <Button
            variant="secondary"
            size="md"
            onClick={handleClose}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSendToClient}
            disabled={isCreating || !isFormComplete()}
          >
            {isCreating && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            {isCreating ? "Sending..." : "Send to client"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GuidelinesPlanCreatePopup;